import React, { useEffect, useState } from "react";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToFirstScrollableAncestor,
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";

import { CSS } from "@dnd-kit/utilities";

export function SortableList({ items, onSorted, renderItem, itemClassName }) {
  const [internalItems, setInternalItems] = useState(items);

  useEffect(() => {
    setInternalItems(items);
  }, [items]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = internalItems.findIndex((item) => item.id === active.id);
      const newIndex = internalItems.findIndex((item) => item.id === over.id);
      const newItems = arrayMove(internalItems, oldIndex, newIndex);
      setInternalItems(newItems);
      onSorted?.(newItems);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[
        restrictToVerticalAxis,
        restrictToFirstScrollableAncestor,
        restrictToParentElement,
      ]}
    >
      <SortableContext
        items={internalItems.map((item) => item.id)}
        strategy={verticalListSortingStrategy}
      >
        {internalItems.map((item) => (
          <SortableItem key={item.id} id={item.id} className={itemClassName}>
            {renderItem(item)}
          </SortableItem>
        ))}
      </SortableContext>
    </DndContext>
  );
}

function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}
